import React, { Component } from 'react';
import { withNamespaces } from 'react-i18next';
import PropTypes from 'prop-types';
import { camelCase, uniqueId, isEmpty, capitalize } from 'lodash';
import classNames from 'classnames';
import ModalWithConfirm from './ModalWithConfirm';
import Loading from '../common/Loading';
import Toast from '../common/Toasts/Toast';
import RoundedTitle from '../common/RoundedTitle';
import DocumentIcon from '../../assets/img/rich_icons/document.svg';
import Attachment from './Attachment';
import TicketsService from '../../services/TicketsService';
import LabelValue from '../common/LabelValue';
import { AttachmentType, genericTicketTypes, regexExp } from '../../utils/Constants';
import ReversalForm from './ReversalForm';
import TransactionsService from '../../services/TransactionsService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

class AddTicketModal extends Component {
  state = {
    ticket: {},
    optionalDocuments: [],
    validationDoc: {},
    ticketDescription: {
      value: '',
      touched: false,
      error: false,
    },
    isLoading: true,
    error: false,
    submitting: false,
    bankCodes: [],
    docTypes: [],
    accountTypes: [],
    genericTicketType: 'GENERAL',
  };

  submitReversalForm = null;

  async componentDidMount() {
    const { service, data = '', showError, onClose, isReversal, country, t } = this.props;
    const promise = service(data);
    promise.then(
      (data) => {
        const { requiredDocuments = [], uploadDocuments = [] } = data;

        const uploaded = uploadDocuments.reduce((prev, doc) => {
          return {
            ...prev,
            [doc.value]: true,
          };
        }, {});

        const documents = requiredDocuments.reduce((prev, doc) => {
          return {
            ...prev,
            [`${camelCase(doc.value)}Attachment`]: uploaded[doc.value] || false,
          };
        }, {});

        this.setState({
          ticket: { ...data },
          isLoading: false,
          validationDoc: { ...documents },
        });
      },
      (e) => {
        this.setState({
          isLoading: false,
          error: true,
        });
        onClose();
        const {
          response: {
            data: { description = '' },
          },
        } = e;
        const msgError = description || 'labelNetworkError';
        showError(msgError);
      }
    );
    if (isReversal) {
      const bankCodesData = await TransactionsService.fetchBankCodes(country);
      const docTypesData = await TransactionsService.fetchDocumentTypes(country);
      const accountTypesData = await TransactionsService.fetchRefundAccountTypes(country);
      const bankCodes = bankCodesData.length
        ? bankCodesData.map((a) => ({
            value: a.name,
            label: capitalize(a.name),
          }))
        : [];
      const docTypes = docTypesData.length ? docTypesData.map((type) => ({ value: type, label: type })) : ['ID'];
      const accountTypes = accountTypesData.length
        ? accountTypesData.map((acc) => ({
            value: acc,
            label: t(acc),
          }))
        : [];
      this.setState({ bankCodes, docTypes, accountTypes });
    }
  }

  bindSubmitForm = (reversalForm) => {
    this.submitReversalForm = reversalForm;
  };

  getModalButtons = () => {
    return [
      {
        name: 'send',
        text: 'buttonSend',
        action: this.clickSendTicket,
      },
      {
        name: 'close',
        text: 'buttonClose',
      },
    ];
  };

  submitTicket = () => {
    const { onClose, afterClose, buttons } = this.props;
    const {
      ticket: { idTicket },
      ticketDescription,
      beneficiary,
      documentType,
      document,
      bank,
      bankBranch,
      accountType,
      accountNumber,
      genericTicketType,
    } = this.state;
    if (this.isTicketValid()) {
      const promise = TicketsService.confirmTicket(
        idTicket,
        ticketDescription.value,
        beneficiary,
        documentType,
        document,
        bank,
        bankBranch,
        accountType,
        accountNumber,
        genericTicketType,
        buttons
      );
      return promise.then(
        () => {
          this.setState({ submitting: false });
          onClose();
          if (typeof afterClose === 'function') {
            afterClose();
          }
        },
        () => {
          onClose();
        }
      );
    }
    return undefined;
  };

  clickSendTicket = () => {
    const { isReversal } = this.props;
    this.setState({ submitting: true });
    if (isReversal) {
      this.submitReversalForm();
    } else {
      this.submitTicket();
    }
  };

  handleCancel = async () => {
    const {
      ticket: { idTicket },
    } = this.state;
    return TicketsService.cancelTicket(idTicket);
  };

  handleDescChange = (e) => {
    const desc = e.target.value;
    this.setState((prevState) => ({
      ticketDescription: {
        ...prevState.ticketDescription,
        value: desc,
        error: desc.toString().trim().length <= 0,
      },
    }));
  };

  handleKeyUp = () => {
    this.setState((prevState) => ({
      ticketDescription: {
        ...prevState.ticketDescription,
        touched: true,
      },
    }));
  };

  handleDocChange = (docName, valid) => {
    this.setState((prevState) => {
      return {
        validationDoc: {
          ...prevState.validationDoc,
          [docName]: valid,
        },
      };
    });
  };

  handleOptionalDocRemove = (docName) => {
    const { validationDoc } = this.state;
    const { [docName]: name, ...tempValidation } = validationDoc;

    this.setState((prevState) => ({
      optionalDocuments: prevState.optionalDocuments.filter((document) => {
        return docName !== `${camelCase(document.name)}Attachment`;
      }),
      validationDoc: {
        ...tempValidation,
      },
    }));
  };

  isTicketValid = () => {
    const { ticketDescription, validationDoc } = this.state;
    const { isReversal } = this.props;

    if (isReversal) {
      return Object.keys(validationDoc).every((key) => {
        return validationDoc[key];
      });
    }

    return (
      ticketDescription.value &&
      !regexExp.test(ticketDescription.value) &&
      Object.keys(validationDoc).every((key) => {
        return validationDoc[key];
      })
    );
  };

  handleUploadChange = (event) => {
    const { t, showError } = this.props;
    const { optionalDocuments } = this.state;
    const file = event.target.files[0];

    // User cancelled upload
    if (!file) {
      event.target.value = null;
      return;
    }

    const prevFile = optionalDocuments.filter((doc) => {
      return doc.name === file.name && doc.size === file.size && doc.type === file.type ? doc : null;
    });
    if (prevFile.length > 0) {
      // Handle error file already uploaded.
      const msgError = t('labelFileAlreadyUploaded');
      showError(msgError);
      event.target.value = null;
      return;
    }

    this.setState((prevState) => ({
      optionalDocuments: [...prevState.optionalDocuments, file],
      validationDoc: {
        ...prevState.validationDoc,
        [`${camelCase(file.name)}Attachment`]: false,
      },
    }));
    event.target.value = null;
  };

  storeValues = (values) => {
    this.setState({ ...values }, () => this.submitTicket());
  };

  renderDescriptionBox = (ticketDescription, invalidDescription, invalidDescriptionError, t) => {
    return (
      <>
        <p className="label ml-1" style={{ fontSize: 10 }}>
          {t('labelEnterDescription').toUpperCase()}
        </p>
        <textarea
          className={`form-control ticket-text mb-2
          ${invalidDescription && 'is-invalid'}`}
          id="ticketDescription"
          autoFocus
          value={ticketDescription.value}
          onChange={this.handleDescChange}
          onKeyUp={this.handleKeyUp}
        />
        {invalidDescription && <div className="invalid-feedback">{t(invalidDescriptionError)}</div>}
      </>
    );
  };

  getSelectTypeTicket = (type) => {
    this.setState({ genericTicketType: genericTicketTypes[type] });
  };

  render() {
    const { isOpen, onClose, t, className, isReversal = false, country, buttons = false } = this.props;
    const { ticket, isLoading, error, ticketDescription, optionalDocuments, submitting, bankCodes, docTypes, accountTypes, genericTicketType } = this.state;
    const { title, description, parameters, idTicket, requiredDocuments, uploadDocuments, prevTicket } = ticket;
    const hasAttachments = !isEmpty(requiredDocuments) || !isEmpty(optionalDocuments);
    const filesClasses = classNames('files', {
      'justify-content-center align-items-center': !hasAttachments,
    });
    const specialCharacters = regexExp.test(ticketDescription.value);
    const invalidDescription = (ticketDescription.error && ticketDescription.touched) || (!ticketDescription.value.length && submitting) || specialCharacters;
    const hasRequiredDocs = !isEmpty(requiredDocuments);
    const showRequiredDocs = !isEmpty(requiredDocuments) && isEmpty(uploadDocuments);
    const invalidDescriptionError = specialCharacters ? 'validationInvalidKey' : 'validationRequired';
    return (
      <>
        {isLoading && <Loading />}
        {!isLoading && !error && (
          <ModalWithConfirm
            isOpen={isOpen}
            title={title}
            onClose={onClose}
            onConfirmCancel={this.handleCancel}
            size="large"
            iconName="file"
            buttons={this.getModalButtons()}
            className={className}
            boxCenter="d-flex justify-content-end m-3"
          >
            {description && (
              <div>
                <Toast text={description} isDismissable={false} color={prevTicket ? 'danger' : 'light'} textDefault iconQuestion />
              </div>
            )}
            <div className="ticket-content">
              <div className="ticket-info">
                {buttons && (
                  <div className="container-buttons mt-2">
                    {Object.values(genericTicketTypes).map((res) => (
                      <input id={`button-${res}`} key={res} class={`btn btn-primary w-18`} type="button" value={res} onClick={() => this.getSelectTypeTicket(res)} />
                    ))}
                  </div>
                )}
                {buttons && <LabelValue value={genericTicketType} label={t('LabelSelectType')} className="mt-2 p-2 select" />}
                {parameters && (
                  <div className="ticket-details">
                    <div className="title mb-2">
                      <RoundedTitle title={t('labelTicketDetails')} className="rounded-title" />
                    </div>
                    {parameters.map((parameter) => {
                      return (
                        <div className="details" key={uniqueId()}>
                          <LabelValue label={`${parameter.key}: `.toUpperCase()} value={parameter.value} className="label-value-no-margin" />
                          <div className="border-line"></div>
                        </div>
                      );
                    })}
                  </div>
                )}
                {isReversal && (
                  <ReversalForm
                    bindSubmitForm={this.bindSubmitForm}
                    t={t}
                    storeValues={this.storeValues}
                    country={country}
                    bankCodes={bankCodes}
                    docTypes={docTypes}
                    accountTypes={accountTypes}
                  />
                )}
                {!isReversal && this.renderDescriptionBox(ticketDescription, invalidDescription, invalidDescriptionError, t)}
              </div>
              <div className="ticket-files">
                <div className="content">
                  {(hasRequiredDocs || isEmpty(optionalDocuments)) && (
                    <div>
                      <RoundedTitle title={hasRequiredDocs ? t('labelRequiredDoc') : t('labelAttachments')} className="rounded-title rounded-title mb-2" required={hasRequiredDocs} />
                    </div>
                  )}
                  <p className="file-types">{t('allowedFileType').toUpperCase()}</p>
                  <div className={filesClasses}>
                    {!hasAttachments && <img src={DocumentIcon} alt="Attachments" className="img-files" />}
                    {showRequiredDocs &&
                      requiredDocuments.map((document) => (
                        <Attachment
                          idParent={idTicket}
                          key={document.value}
                          id={document.value}
                          name={document.key}
                          isRequired
                          onChange={this.handleDocChange}
                          showRequiredMsg={submitting}
                        />
                      ))}
                    {!isEmpty(uploadDocuments) &&
                      uploadDocuments.map((document) => <Attachment key={document.value} id={document.idAttachment} name={document.key} type={AttachmentType.DOWNLOAD} />)}
                    {!isEmpty(optionalDocuments) && (
                      <>
                        <div>
                          <RoundedTitle title={t('labelOptionalDoc')} className="rounded-title rounded-title-darker mb-2" />
                        </div>
                        {optionalDocuments.map((document) => {
                          return (
                            <Attachment
                              idParent={idTicket}
                              id={document.name}
                              key={document.name}
                              name={document.name}
                              fileName={document.name}
                              isRequired={false}
                              file={document}
                              onChange={this.handleDocChange}
                              onRemove={this.handleOptionalDocRemove}
                            />
                          );
                        })}
                      </>
                    )}
                  </div>
                </div>
                <div className="footer">
                  <input
                    ref={(ref) => {
                      this.inputMultiFile = ref;
                    }}
                    type="file"
                    id="extraAttachments"
                    accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, image/jpeg,image/png,application/pdf"
                    onChange={this.handleUploadChange}
                  />
                  <button
                    type="button"
                    className="btn btn-dark btn-files"
                    onClick={() => {
                      this.inputMultiFile.click();
                    }}
                  >
                    {t('buttonUploadExtra')}
                    <FontAwesomeIcon className="ml-4 mt-2" icon="arrow-up" size="sm" />
                  </button>
                </div>
              </div>
            </div>
          </ModalWithConfirm>
        )}
      </>
    );
  }
}

AddTicketModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  service: PropTypes.func.isRequired,
  data: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number]))]),
  isReversal: PropTypes.bool,
  afterClose: PropTypes.func,
  country: PropTypes.string,
  buttons: PropTypes.bool,
  boxCenter: PropTypes.string,
};

export default withNamespaces()(AddTicketModal);
